import { useAppDispatch, useAppSelector } from 'store/hooks';
import React from 'react';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import {
    setSelectedRow,
    setItemToReset,
    setItemToDelete,
    setOpenItem,
    setItemToDuplicate,
    setPerkAwardCount
} from 'store/perks/perksSlice';
import { perkApi } from 'components/loyalty/PerkApi';
import { isNumber } from 'lib/typeguards';

export const usePepperPerksUtils = () => {
    const dispatch = useAppDispatch();
    const { perks, selectedRow } = useAppSelector(state => state.perks);

    const selectedPerk = React.useMemo(
        () => perks.find(perk => perk._id === selectedRow[0]),
        [perks, selectedRow]
    );

    const handleSelectRow = React.useCallback(
        (newSelectedRow: GridRowSelectionModel) => {
            // Enforces single selection
            if (newSelectedRow.length > 1) {
                dispatch(setSelectedRow([newSelectedRow[newSelectedRow.length - 1]]));
            } else {
                dispatch(setSelectedRow(newSelectedRow));
            }
        },
        [dispatch]
    );

    const handleSetItemToReset = React.useCallback(
        (index: number) => dispatch(setItemToReset(index)),
        [dispatch]
    );

    const handleSetItemToDelete = React.useCallback(
        (index: number) => dispatch(setItemToDelete(index)),
        [dispatch]
    );

    const handleSetOpenItem = React.useCallback((index: number) => dispatch(setOpenItem(index)), [dispatch]);

    const handleSetItemToDuplicate = React.useCallback(
        (index: number) => dispatch(setItemToDuplicate(index)),
        [dispatch]
    );

    const handleReviewAndEditClick = React.useCallback(() => {
        const index = perks.findIndex(perk => perk._id === selectedPerk._id);

        handleSetOpenItem(index);
    }, [perks, selectedPerk, handleSetOpenItem]);

    const handleDuplicateClick = React.useCallback(() => {
        const index = perks.findIndex(perk => perk._id === selectedPerk._id);

        handleSetItemToDuplicate(index);
    }, [perks, selectedPerk, handleSetItemToDuplicate]);

    const handleResetClick = React.useCallback(async () => {
        if (!selectedPerk) {
            return;
        }

        const index = perks.findIndex(perk => perk._id === selectedPerk._id);
        handleSetItemToReset(index);

        const count = await getPerkAwardCount(selectedPerk._id);
        dispatch(setPerkAwardCount(count));
    }, [dispatch, perks, selectedPerk, handleSetItemToReset]);

    const handleDeleteClick = React.useCallback(() => {
        const index = perks.findIndex(perk => perk._id === selectedPerk._id);

        handleSetItemToDelete(index);
    }, [perks, selectedPerk, handleSetItemToDelete]);

    return {
        selectedRow,
        selectedPerk,
        handleSelectRow,
        handleReviewAndEditClick,
        handleResetClick,
        handleDeleteClick,
        handleDuplicateClick
    };
};

const getPerkAwardCount = async (perkId: string): Promise<number> => {
    const response = await perkApi.getPointPerk(perkId, {
        queryParameters: [{ key: 'include', value: 'awardCount' }]
    });

    if (response.ok) {
        if (isNumber(response.body.awardCount)) {
            return response.body.awardCount;
        }
        throw new Error(`Expected Perk but got ${JSON.stringify(response.body)}`);
    }

    throw new Error(response.body.message);
};
